import { selectVisibleDeviceTypes } from 'features/Integrations/state/selectors/integrationSelectors';
import { useEffect, useRef, useState } from 'react';
import { Button, Collapse } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import DeviceRow from './components/DeviceRow/DeviceRow';
import DeviceRowAccordion from './components/DeviceRowAccordion/DeviceRowAccordion';
import { FaChevronDown } from 'react-icons/fa6';

const COLLAPSE_ANIMATION_MS = 350;

const DeviceTableHeader = () => (
  <div className="d-none d-lg-flex row px-2 py-2 mx-0 small fw-semibold text-theme-muted border-bottom border-theme-strong bg-primary-shadow">
    <div className="col-lg-4">Name</div>
    <div className="col-lg-3">ID</div>
    <div className="col-lg-3">Status</div>
    <div className="col-lg-2" />
  </div>
);

function DeviceTypeSection({
  devices,
  sectionTitle,
  onOpenDevice,
  widget,
  hideHeader,
  deviceAccordion,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isAnimatingRef = useRef(false);
  const animationGuardTimeoutRef = useRef(null);

  useEffect(() => {
    return () => window.clearTimeout(animationGuardTimeoutRef.current);
  }, []);

  const releaseAnimationGuard = () => {
    isAnimatingRef.current = false;
    window.clearTimeout(animationGuardTimeoutRef.current);
  };

  const onToggleSection = () => {
    if (isAnimatingRef.current) return;

    isAnimatingRef.current = true;
    setIsOpen((prev) => !prev);
    window.clearTimeout(animationGuardTimeoutRef.current);
    animationGuardTimeoutRef.current = window.setTimeout(
      releaseAnimationGuard,
      COLLAPSE_ANIMATION_MS,
    );
  };

  if (hideHeader) {
    return deviceAccordion ? (
      <DeviceRowAccordion widget={widget} devices={devices} onOpenDevice={onOpenDevice} />
    ) : (
      <DeviceRow widget={widget} devices={devices} onOpenDevice={onOpenDevice} />
    );
  }

  const sectionId = `device-type-section-${sectionTitle.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

  return (
    <div className="py-1">
      <Button
        className={`w-100 text-center border border-theme-strong py-2 bg-primary text-white d-flex align-items-center justify-content-center gap-2 m-0 ${
          isOpen ? 'rounded-top-2 rounded-bottom-0' : 'rounded-2'
        }`}
        style={{
          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.18), 0 2px 4px rgba(0, 0, 0, 0.24)',
          position: 'relative',
          zIndex: 1,
        }}
        onClick={onToggleSection}
        aria-expanded={isOpen}
        aria-controls={sectionId}
      >
        <span className="fs-6 fw-semibold">{sectionTitle.replace('_', ' ')}</span>
        <FaChevronDown
          size={13}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: `transform ${COLLAPSE_ANIMATION_MS}ms ease`,
          }}
        />
      </Button>

      <Collapse in={isOpen} onEntered={releaseAnimationGuard} onExited={releaseAnimationGuard}>
        <div id={sectionId}>
          <div
            className="border border-top-0 border-theme-strong rounded-bottom-2 overflow-hidden bg-primary-shadow"
            style={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? 'translateY(0)' : 'translateY(-4px)',
              transformOrigin: 'top center',
              transition: `opacity ${COLLAPSE_ANIMATION_MS}ms ease, transform ${COLLAPSE_ANIMATION_MS}ms ease`,
              willChange: 'opacity, transform',
            }}
          >
            {!deviceAccordion && <DeviceTableHeader />}
            {deviceAccordion ? (
              <DeviceRowAccordion widget={widget} devices={devices} onOpenDevice={onOpenDevice} />
            ) : (
              <DeviceRow widget={widget} devices={devices} onOpenDevice={onOpenDevice} />
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
}

/**
 * @param {{type: object, name: string, devices?: number[], typeName?: string, depth?: number, onOpenDevice: ()}}
 * @returns
 */
export default function DeviceTypeList({
  system,
  systemName,
  onOpenDevice,
  widget = false,
  hideHeader = false,
  deviceAccordion = false,
}) {
  // system is now a single device type object
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const [params] = useSearchParams();
  const searchTerm = params.get('search')?.replace(/\s+/g, '').toLowerCase() || '';

  if (!system || typeof system !== 'object') {
    console.error('Error in device type');
    return null;
  }

  let devicesForThisType = connectedDevices.filter(
    (device) => device.device_type === system.device_type,
  );

  if (visibleDeviceTypes) {
    const isDeviceTypeVisible = visibleDeviceTypes[system.device_type] !== false;
    if (!isDeviceTypeVisible) return null;
  }

  if (searchTerm) {
    devicesForThisType = devicesForThisType.filter((device) => {
      return (
        device.alias?.toLowerCase().replace(/\s/g, '').includes(searchTerm) ||
        device.name?.toLowerCase().replace(/\s/g, '').includes(searchTerm) ||
        String(device.node_id).includes(searchTerm)
      );
    });
  }

  devicesForThisType.sort((a, b) => a.node_id - b.node_id);

  if (devicesForThisType.length === 0) return null;
  const sectionTitle = `${system.system_type} - ${system.device_type}`;

  return (
    <DeviceTypeSection
      key={sectionTitle}
      devices={devicesForThisType}
      sectionTitle={sectionTitle}
      onOpenDevice={onOpenDevice}
      widget={widget}
      hideHeader={hideHeader}
      deviceAccordion={deviceAccordion}
    />
  );
}
